import { ParticipantResourceSelection, ResourceOption } from '@kouto/types';
import { ICartExperience } from 'types/cart';

type Combination = ResourceOption['combinations'][number];

export type ParticipantSelections = ParticipantResourceSelection[][];

export interface ResourceParticipant {
  priceTierName: string;
  resourceSelections?: ParticipantResourceSelection[];
}

export interface ResourceSlot {
  options: ResourceOption[];
  participantIndex: number;
  priceTierName: string;
  unitNumber: number;
}

export const doesResourceOptionApplyToTier = (
  option: ResourceOption,
  priceTierName: string,
) =>
  option.attributes.length > 0 &&
  (option.priceTierNames === null ||
    option.priceTierNames.includes(priceTierName));

export const deriveResourceSlots = (
  participants: ResourceParticipant[],
  options: ResourceOption[],
): ResourceSlot[] =>
  participants.flatMap((participant, participantIndex) => {
    const applicable = options.filter((option) =>
      doesResourceOptionApplyToTier(option, participant.priceTierName),
    );
    if (!applicable.length) return [];
    const unitNumber =
      participants
        .slice(0, participantIndex)
        .filter((other) => other.priceTierName === participant.priceTierName)
        .length + 1;
    return [
      {
        options: applicable,
        participantIndex,
        priceTierName: participant.priceTierName,
        unitNumber,
      },
    ];
  });

export const hasResourceOptionsForParticipants = (
  participants: ResourceParticipant[],
  options: ResourceOption[],
) => deriveResourceSlots(participants, options).length > 0;

export const findResourcePick = (
  selections: ParticipantResourceSelection[] | undefined,
  resourcePoolId: string,
  resourceAttributeId: string,
) =>
  selections?.find(
    (selection) =>
      selection.resourcePoolId === resourcePoolId &&
      selection.resourceAttributeId === resourceAttributeId,
  );

const toPickedValueIds = (
  selections: ParticipantResourceSelection[] | undefined,
  option: ResourceOption,
  attributeCount = option.attributes.length,
) =>
  option.attributes.slice(0, attributeCount).flatMap((attribute) => {
    const pick = findResourcePick(
      selections,
      option.resourcePoolId,
      attribute.resourceAttributeId,
    );
    return pick ? [pick.resourceAttributeValueId] : [];
  });

const fitsCombination = (valueIds: string[], combination: Combination) =>
  valueIds.every((valueId) => combination.valueIds.includes(valueId));

/**
 * Each participant needs its own unit, so a set of picks is only possible when every participant can
 * be matched to a distinct free unit (bipartite matching over the combinations' free counts).
 */
export const canAssignUnits = (
  demands: string[][],
  combinations: Combination[],
) => {
  const assigned = combinations.map(() => [] as number[]);

  const tryAssign = (demandIndex: number, visited: Set<number>): boolean =>
    combinations.some((combination, combinationIndex) => {
      if (
        visited.has(combinationIndex) ||
        !fitsCombination(demands[demandIndex], combination)
      ) {
        return false;
      }
      visited.add(combinationIndex);
      const occupants = assigned[combinationIndex];
      if (occupants.length < combination.available) {
        occupants.push(demandIndex);
        return true;
      }
      return occupants.some((occupant, position) => {
        if (!tryAssign(occupant, visited)) return false;
        occupants[position] = demandIndex;
        return true;
      });
    });

  return demands.every((_, demandIndex) => tryAssign(demandIndex, new Set()));
};

interface SelectableValueIdsParams {
  attributeIndex: number;
  option: ResourceOption;
  participantIndex: number;
  selections: ParticipantSelections;
  slots: ResourceSlot[];
}

/**
 * Values stay selectable when they fit the participant's earlier picks for this resource and still
 * leave a unit for every other participant's current picks.
 */
export const deriveSelectableValueIds = ({
  attributeIndex,
  option,
  participantIndex,
  selections,
  slots,
}: SelectableValueIdsParams) => {
  const optionSlots = slots.filter((slot) => slot.options.includes(option));
  const earlierPicks = toPickedValueIds(
    selections[participantIndex],
    option,
    attributeIndex,
  );
  const attribute = option.attributes[attributeIndex];

  return new Set(
    attribute.values
      .filter(({ resourceAttributeValueId }) => {
        const demands = optionSlots.map((slot) =>
          slot.participantIndex === participantIndex
            ? [...earlierPicks, resourceAttributeValueId]
            : toPickedValueIds(selections[slot.participantIndex], option),
        );
        return canAssignUnits(demands, option.combinations);
      })
      .map(({ resourceAttributeValueId }) => resourceAttributeValueId),
  );
};

type EarlierPicksParams = Omit<SelectableValueIdsParams, 'slots'>;

/**
 * Values some unit carries together with the participant's earlier picks, booked or not. A value
 * outside this set is ruled out by those picks, not sold out.
 */
export const deriveCompatibleValueIds = ({
  attributeIndex,
  option,
  participantIndex,
  selections,
}: EarlierPicksParams) => {
  const earlierPicks = toPickedValueIds(
    selections[participantIndex],
    option,
    attributeIndex,
  );
  return new Set(
    option.attributes[attributeIndex].values
      .map(({ resourceAttributeValueId }) => resourceAttributeValueId)
      .filter((valueId) =>
        option.combinations.some((combination) =>
          fitsCombination([...earlierPicks, valueId], combination),
        ),
      ),
  );
};

export const deriveEarlierPickLabels = ({
  attributeIndex,
  option,
  participantIndex,
  selections,
}: EarlierPicksParams) => {
  const earlierPicks = toPickedValueIds(
    selections[participantIndex],
    option,
    attributeIndex,
  );
  return option.attributes
    .slice(0, attributeIndex)
    .flatMap(({ values }) =>
      values.filter(({ resourceAttributeValueId }) =>
        earlierPicks.includes(resourceAttributeValueId),
      ),
    )
    .map(({ label }) => label);
};

interface ApplyResourcePickParams {
  option: ResourceOption;
  participantIndex: number;
  resourceAttributeId: string;
  resourceAttributeValueId: string;
  selections: ParticipantSelections;
  slots: ResourceSlot[];
}

/** Sets one pick, then drops later picks of the same resource that no longer fit (cascade). */
export const applyResourcePick = ({
  option,
  participantIndex,
  resourceAttributeId,
  resourceAttributeValueId,
  selections,
  slots,
}: ApplyResourcePickParams): ParticipantSelections => {
  const changedIndex = option.attributes.findIndex(
    (attribute) => attribute.resourceAttributeId === resourceAttributeId,
  );
  const current = selections[participantIndex] ?? [];
  const otherResources = current.filter(
    (selection) => selection.resourcePoolId !== option.resourcePoolId,
  );

  const nextResourcePicks = option.attributes.reduce<
    ParticipantResourceSelection[]
  >((picks, attribute, attributeIndex) => {
    if (attributeIndex < changedIndex) {
      const pick = findResourcePick(
        current,
        option.resourcePoolId,
        attribute.resourceAttributeId,
      );
      return pick ? [...picks, pick] : picks;
    }
    if (attributeIndex === changedIndex) {
      return [
        ...picks,
        {
          resourcePoolId: option.resourcePoolId,
          resourceAttributeId,
          resourceAttributeValueId,
        },
      ];
    }
    const laterPick = findResourcePick(
      current,
      option.resourcePoolId,
      attribute.resourceAttributeId,
    );
    if (!laterPick) return picks;
    const selectable = deriveSelectableValueIds({
      attributeIndex,
      option,
      participantIndex,
      selections: selections.map((participantSelections, index) =>
        index === participantIndex ? picks : participantSelections,
      ),
      slots,
    });
    return selectable.has(laterPick.resourceAttributeValueId)
      ? [...picks, laterPick]
      : picks;
  }, []);

  return selections.map((participantSelections, index) =>
    index === participantIndex
      ? [...otherResources, ...nextResourcePicks]
      : participantSelections,
  );
};

/** Aligns picks to the participants, keeping only picks that still match an offered option and value. */
export const sanitizeResourceSelections = (
  participants: ResourceParticipant[],
  options: ResourceOption[],
): ParticipantSelections =>
  participants.map((participant) =>
    (participant.resourceSelections ?? []).filter((selection) =>
      options.some(
        (option) =>
          option.resourcePoolId === selection.resourcePoolId &&
          doesResourceOptionApplyToTier(option, participant.priceTierName) &&
          option.attributes.some(
            (attribute) =>
              attribute.resourceAttributeId === selection.resourceAttributeId &&
              attribute.values.some(
                (value) =>
                  value.resourceAttributeValueId ===
                  selection.resourceAttributeValueId,
              ),
          ),
      ),
    ),
  );

export const isResourceSelectionComplete = (
  slots: ResourceSlot[],
  selections: ParticipantSelections,
) =>
  slots.every((slot) =>
    slot.options.every((option) =>
      option.attributes.every((attribute) =>
        findResourcePick(
          selections[slot.participantIndex],
          option.resourcePoolId,
          attribute.resourceAttributeId,
        ),
      ),
    ),
  );

export const INSUFFICIENT_RESOURCE_ERROR_TYPE = 'InsufficientResourceException';

/** The participants page of the first cart item whose guests picked resource values. */
export const deriveResourcePicksPagePath = (cartItems: ICartExperience[]) => {
  const cartItem = cartItems.find((item) =>
    item.participants.some(
      (participant) => participant.resourceSelections?.length,
    ),
  );
  return cartItem
    ? `/e/${cartItem.experienceId}/participants/${cartItem.cartItemId}`
    : null;
};
